import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { IAccordionProtectedItem } from '../components/Accordion/Protected/interfaces';

interface IUseAccordionProtected {
  componentId?: string;
  initialItems?: IAccordionProtectedItem[];
  pageApiUrl?: string;
}

interface IAccordionProtectedState {
  error: boolean;
  items: IAccordionProtectedItem[];
  loaded: boolean;
  loading: boolean;
}

const createInitialState = (
  items: IAccordionProtectedItem[] = []
): IAccordionProtectedState => ({
  error: false,
  items,
  loaded: false,
  loading: false,
});

const requestProtectedItems = async (
  token: string,
  pageApiUrl?: string,
  componentId?: string
): Promise<IAccordionProtectedItem[]> => {
  if (!pageApiUrl || !componentId) {
    throw new Error('Missing protected accordion endpoint.');
  }

  const endpoint = `${pageApiUrl.replace(/\/?$/, '/')}` +
    `accordion_protected/${componentId}/`;
  const { data } = await axios.post(endpoint, { recaptcha_token: token });

  if (!Array.isArray(data?.items)) {
    throw new Error('Invalid protected accordion response.');
  }

  return data.items;
};

export const useAccordionProtected = ({
  componentId,
  initialItems = [],
  pageApiUrl,
}: IUseAccordionProtected) => {
  const [ state, setState ] = useState<IAccordionProtectedState>(
    createInitialState(initialItems)
  );
  const pendingRequest = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    setState(createInitialState(initialItems));
    pendingRequest.current = null;
  }, [ componentId, initialItems ]);

  const verifyCaptcha = useCallback(async (token: string): Promise<boolean> => {
    if (state.loaded) return true;
    if (pendingRequest.current) return pendingRequest.current;

    setState((current) => ({ ...current, error: false, loading: true }));

    const request = requestProtectedItems(token, pageApiUrl, componentId)
      .then((items) => {
        setState({ error: false, items, loaded: true, loading: false });
        return true;
      })
      .catch(() => {
        setState((current) => ({
          ...current,
          error: true,
          loading: false,
        }));
        return false;
      })
      .finally(() => {
        pendingRequest.current = null;
      });

    pendingRequest.current = request;
    return request;
  }, [ componentId, pageApiUrl, state.loaded ]);

  return {
    error: state.error,
    items: state.items,
    loading: state.loading,
    locked: !state.loaded,
    verifyCaptcha,
  };
};
